// src/pages/teacher/TeacherLivePage.jsx
import { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Alert, LinearProgress,
  Table, TableHead, TableRow, TableCell, TableBody,
  Chip, IconButton, Tooltip, Button, Snackbar,
} from '@mui/material';
import LiveTvIcon   from '@mui/icons-material/LiveTv';
import StopIcon     from '@mui/icons-material/Stop';
import RefreshIcon  from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { getLiveSessions, stopStudentSession } from '../../api/teacherApi';

function ElapsedBar({ elapsedMin, remainingMin }) {
  const total = (elapsedMin || 0) + (remainingMin || 60);
  const pct   = Math.min(100, Math.round((elapsedMin / total) * 100));
  const color = remainingMin != null && remainingMin < 10 ? '#ef4444'
              : remainingMin != null && remainingMin < 20 ? '#f59e0b' : '#0f766e';
  return (
    <Stack spacing={0.5} sx={{ minWidth: 120 }}>
      <LinearProgress variant="determinate" value={pct}
        sx={{ height: 5, borderRadius: 2, bgcolor: '#e2e8f0',
          '& .MuiLinearProgress-bar': { bgcolor: color } }} />
      <Typography fontSize={11} color="text.secondary">
        {elapsedMin}min ecoules
        {remainingMin != null ? ' · ' + remainingMin + 'min restantes' : ''}
      </Typography>
    </Stack>
  );
}

export default function TeacherLivePage() {
  const { t }          = useTranslation('teacher');
  const [sessions, setSessions] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [stopping, setStopping] = useState({});
  const [snack,    setSnack]    = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getLiveSessions();
      setSessions(r.data?.sessions || []);
    } catch(e) {
      setError(e?.response?.data?.detail?.message || t('errorLoad'));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // refresh auto 30s
    return () => clearInterval(id);
  }, [load]);

  const handleStop = async (resId, username) => {
    setStopping(s => ({ ...s, [resId]: true }));
    try {
      await stopStudentSession(resId);
      setSnack(t('sessionStopped', { user: username }));
      await load();
    } catch(e) {
      setError(e?.response?.data?.detail?.message || t('errorStop'));
    } finally { setStopping(s => ({ ...s, [resId]: false })); }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: 3, py: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <LiveTvIcon sx={{ fontSize: 28, color: '#0f766e' }} />
          <Typography variant="h5" fontWeight={700}>{t('liveSessions')}</Typography>
          <Chip label={sessions.length + ' actives'}
            sx={{ bgcolor: '#f0fdf4', color: '#0f766e', fontWeight: 600 }} />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography fontSize={12} color="text.secondary">
            {t('autoRefresh30s', { defaultValue: 'Refresh auto 30s' })}
          </Typography>
          <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
        </Stack>
      </Stack>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && sessions.length === 0 && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {sessions.length === 0 && !loading ? (
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <LiveTvIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
          <Typography color="text.secondary">{t('noActiveSessions')}</Typography>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell>{t('student')}</TableCell>
                <TableCell>{t('lab')}</TableCell>
                <TableCell>{t('progress')}</TableCell>
                <TableCell>{t('started')}</TableCell>
                <TableCell>{t('projectId')}</TableCell>
                <TableCell>{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map(s => (
                <TableRow key={s.reservation_id} hover
                  sx={{ bgcolor: s.remaining_min != null && s.remaining_min < 10
                    ? '#fff5f5' : 'inherit' }}>
                  <TableCell>
                    <Box>
                      <Typography fontSize={13} fontWeight={600}>
                        {s.full_name || s.username}
                      </Typography>
                      <Typography fontSize={11} color="text.secondary">{s.email}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize={13} fontWeight={500}>{s.lab_name}</Typography>
                    <Typography fontSize={11} color="text.secondary">#{s.lab_id}</Typography>
                  </TableCell>
                  <TableCell>
                    <ElapsedBar elapsedMin={s.elapsed_min} remainingMin={s.remaining_min} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {s.start_time ? new Date(s.start_time).toLocaleTimeString('fr') : '-'}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>
                    {s.project_id ? s.project_id.slice(0, 8) + '…' : '-'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={t('stopSession')}>
                      <span>
                        <IconButton size="small"
                          onClick={() => handleStop(s.reservation_id, s.username)}
                          disabled={!!stopping[s.reservation_id]}
                          sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}>
                          <StopIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Snackbar open={!!snack} autoHideDuration={4000}
        onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}
